import { html, useState } from '/vendor/htm-preact.module.js';
import { useRead } from '/read.js';
import { KeepPage } from '/shortcuts.js';
import { bySeason, seasonLabel, seasonId } from '/seasons.js';
import { Icon } from '/icons.js';
import { Picture } from '/imageupload.js';

/**
 * Which symbol an accolade wears — GitHub issue #57.
 *
 * Matched on the competition's NAME, because that is the only thing the
 * archive tells us about what kind of thing was won: `league_honour` records
 * a competition and a winner, and nothing anywhere classifies a competition
 * as a cup or a plate. So this reads the name, in order, and falls back to a
 * trophy — which is right for anything won, and wrong for nothing.
 *
 * A plate is the consolation event for first-round losers, so it gets its own
 * mark rather than the same trophy as the main draw: a screen that drew them
 * identically would tell somebody they had won the cup when they had not.
 */
const MARKS = [
  [/plate/i, 'shield', 'Plate'],
  [/\bcup\b/i, 'trophy', 'Cup'],
  [/trophy/i, 'trophy', 'Trophy'],
  [/knockout|championship/i, 'crown', 'Knockout'],
];

const markFor = (competition) =>
  (MARKS.find(([re]) => re.test(competition || '')) || [null, 'trophy', 'Won'])[1];

/**
 * One entry per COMPETITION, carrying the seasons it was won in.
 *
 * Grouped rather than listed flat: somebody who has won the same cup four
 * times has won one thing four times, and four identical rows say that worse
 * than one row with a four on it. The seasons are kept so the detail is not
 * lost to the summary.
 */
function grouped(honours) {
  const by = new Map();
  for (const h of honours || []) {
    const key = h.competition;
    if (!by.has(key)) by.set(key, { competition: key, seasons: [] });
    by.get(key).seasons.push(h.season);
  }
  return [...by.values()].sort(
    (a, b) => b.seasons.length - a.seasons.length
              || a.competition.localeCompare(b.competition));
}

/**
 * One player's page inside a league: who they are, their all-time and
 * this-season ratings, the seasons before this one, and their last five
 * matches.
 *
 * `/api/leagues/{league_id}/players/{player_key}` (see cueforge/api.py's
 * `league_player`) already carries the whole shape this draws — this screen
 * only has to lay it out.
 *
 * Reached only from a name the league's own data produced -- a row on the
 * Stats board, eventually a fixture's sheet -- never typed, so a key
 * matching nobody is a broken link rather than a real absence. The API 404s
 * it, and this screen's error state is the same plain server error every
 * other screen here shows for one.
 */
export function PlayerScreen({ leagueId, playerKey, onGo, onDone }) {
  const [player, error] = useRead(
    `/api/leagues/${encodeURIComponent(leagueId)}`
    + `/players/${encodeURIComponent(playerKey)}`);
  // Which season's matches are on show (issue #31). Declared HERE, above the
  // guards below, because the two early returns would otherwise make this a
  // conditional hook — the one thing a component may not do.
  //
  // `undefined` means "whichever is current", so this does not have to wait
  // for the fetch to name a default, and a league that opens a new season
  // does not leave anybody pinned to the old one.
  const [season, setSeason] = useState(undefined);

  if (error) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="player-error">
          ${error.detail || error.message}
        </p>
      </div>`;
  }
  if (player === undefined) return html`<p class="sub">Loading…</p>`;

  const { season: thisSeason, all_time: allTime } = player;

  // Newest season first, so `seasons[0]` is the one being played now. Nothing
  // here reads a league-wide "current season": the newest season these
  // matches belong to IS the current one for this player, and a player who
  // sat out a season should not open on an empty tab because the league has
  // moved on without them.
  const seasons = bySeason(player.recent);
  const here = seasons.find((s) => s.season === season)
    || seasons[0] || { season: null, rows: [] };

  return html`
    <div class="screen" data-testid="player-screen">
      <div class="rowbetween">
        ${/* The face of whoever holds this archive identity -- see
              `avatar_for_league_player`. Most names on a mirrored league's
              board belong to no account at all, so `Picture` draws nothing
              and this heading is the same one line it has always been; the
              flex wrapper only ever has the `h2` in it. A player nobody
              holds must NOT borrow a placeholder's face, which is why the
              lookup goes through the real holder. */''}
        <span style="display:flex;align-items:center;gap:.6rem"
              data-testid="player-face">
          <${Picture} imageId=${player.avatar} alt=${player.name}
                      className="avatar" style="width:2.5rem;height:2.5rem" />
          <h2 data-testid="player-name">${player.name}</h2>
        </span>
        ${/* Issue #77, wrapped with Back for the reason team.js gives:
              `.rowbetween` expects two children, and the face beside the
              name is already one of them. */''}
        <span class="row" style="width:auto">
          <${KeepPage} variant="plain" />
          <button class="secondary" style="width:auto"
                  data-testid="player-back" onClick=${onDone}>Back</button>
        </span>
      </div>
      ${/* `teams` (see cueforge/api.py's `league_player`) is every team
            this player turned out for THIS season, not just one picked by
            row order -- a player fielded in three divisions for three
            teams in one season (their numbers are the whole season's,
            identical on all three rows; only the team/division NAME
            differs) shows all three here, comma-joined in one line rather
            than a list needing its own `.stack` gap. `teams` always has
            at least one entry, and joining a single one reproduces
            exactly what this line used to render for the one-team case --
            no "and 2 more" noise where there is nothing more to report. */''}
      <p class="sub" data-testid="player-team">
        ${player.teams.map(teamLine).join(', ')}
      </p>

      ${/* One star per competition this player has won -- Task 3's
            `honours: [{competition, season}]` (see cueforge/api.py's
            `league_player`). A player who has won nothing gets no section
            at all here, not a heading over an empty list -- the same
            "missing thing renders nothing" rule `player-previous` already
            follows just below. A pair honour's `entrant_key` is NULL at
            rest and so never resolves to a player at all (see
            `readmodel.honours_of`'s own docstring); this section simply
            never has a row to draw for one, on ANY player's page.

            The `player.honours &&` half of this guard is not redundant --
            `honours` (like `previous` and `recent`, guarded the same way
            below) is a key an OLDER server never sent. web_dir is served
            fresh off disk on every request while the read model only picks
            up a restart, so a browser can load this screen's current JS
            against a process that predates the field it expects -- a real,
            recurring skew here, not a hypothetical one (see GitHub issue
            #9). Missing entirely must render the same absent section an
            empty array does, not `Cannot read properties of undefined
            (reading 'length')`. */''}
      ${/* ACCOLADES — issue #57. This section existed and had no HEADING, so
            a row of starred cards sat under the team line looking like
            something the page had failed to label. Naming it is most of the
            fix; the symbols are the rest.

            Grouped by competition, so four wins of one cup read as one thing
            won four times. The seasons stay on the row, because "which years"
            is the next question anybody asks. */''}
      ${player.honours && player.honours.length > 0 && html`
        <div class="stack" data-testid="player-honours">
          <label>Accolades</label>
          ${grouped(player.honours).map((a) => html`
            <div class="card rowbetween"
                 key=${a.competition}
                 data-testid=${`player-honour-${
                   a.competition.replace(/\s+/g, '-')}`}>
              <span style="display:flex;align-items:center;gap:.5rem;
                           min-width:0">
                <span class="brassc" style="display:flex;flex:none"
                      data-testid=${`player-accolade-mark-${
                        a.competition.replace(/\s+/g, '-')}`}>
                  <${Icon} name=${markFor(a.competition)} size=${16} />
                </span>
                <span style="min-width:0">
                  <span style="display:block">${a.competition}</span>
                  <span class="sub" style="text-transform:none">
                    ${a.seasons.slice().sort().join(' · ')}
                  </span>
                </span>
              </span>
              ${a.seasons.length > 1 && html`
                <span class="mono brassc"
                      data-testid=${`player-honour-count-${
                        a.competition.replace(/\s+/g, '-')}`}>
                  ${`×${a.seasons.length}`}
                </span>`}
            </div>`)}
        </div>`}

      <div class="stack" data-testid="player-rating">
        <label>Rating</label>
        <div class="card rowbetween">
          <span>All-time</span>
          <span class="mono pts" data-testid="player-alltime-combined">
            ${elo(allTime.combined)}
          </span>
        </div>
        <div class="rowbetween">
          <span class="sub">Singles</span>
          <span class="mono sub" data-testid="player-alltime-singles">
            ${elo(allTime.singles)}
          </span>
        </div>
        <div class="rowbetween">
          <span class="sub">Doubles</span>
          <span class="mono sub" data-testid="player-alltime-doubles">
            ${elo(allTime.doubles)}
          </span>
        </div>
      </div>

      <div class="stack" data-testid="player-season">
        <label>${`This season — ${thisSeason.name}`}</label>
        <p class="sub" data-testid="player-season-appearances">
          ${`${cell(thisSeason.appearances)} appearances`}
        </p>
        <div class="rowbetween">
          <span class="sub">Singles</span>
          <span class="mono sub" data-testid="player-season-singles">
            ${wl(thisSeason.singles)}
          </span>
        </div>
        <div class="rowbetween">
          <span class="sub">Doubles</span>
          <span class="mono sub" data-testid="player-season-doubles">
            ${wl(thisSeason.doubles)}
          </span>
        </div>
        <div class="rowbetween">
          <span class="sub">Combined</span>
          <span class="mono pts" data-testid="player-season-combined">
            ${wl(thisSeason.combined)}
          </span>
        </div>
      </div>

      ${player.previous && player.previous.length > 0 && html`
        <div class="stack" data-testid="player-previous">
          <label>Previous seasons</label>
          ${player.previous.map((p) => html`
            <div class="card rowbetween" key=${p.season}
                 data-testid=${`player-previous-${
                   p.season.replace(/\s+/g, '-')}`}>
              <span>
                <span style="display:block">${p.season}</span>
                <span class="sub">
                  ${p.teams.map(teamLine).join(', ')}
                </span>
              </span>
              <span class="mono sub">
                ${`${pct(p.combined_win_pct)} · `}
                ${cell(p.combined_elo != null
                  ? Math.round(p.combined_elo) : null)}
              </span>
            </div>`)}
        </div>`}

      ${seasons.length > 0 && html`
        <div class="stack" data-testid="player-recent">
          ${/* Not "Last five" any more (issue #31, second round): every
                match of the season on show. The heading has to say what is
                under it, or the season buttons look like they are filtering
                a five-match list. */''}
          <label>${here.season ? `Matches — ${here.season}` : 'Matches'}</label>
          ${/* Issue #31: the season being played now, with earlier ones
                behind tabs -- and only when there IS more than one. A single
                tab is not a choice, it is decoration, which is the rule the
                team's own fixture piles already follow. */''}
          ${seasons.length > 1 && html`
            <nav class="rooms" data-testid="player-recent-seasons">
              ${seasons.map((s) => html`
                <button key=${s.season || 'unfiled'}
                        class=${here.season === s.season ? '' : 'secondary'}
                        aria-current=${here.season === s.season
                          ? 'page' : undefined}
                        data-testid=${`player-recent-season-${
                          seasonId(s.season)}`}
                        onClick=${() => setSeason(s.season)}>
                  ${seasonLabel(s.season)}
                </button>`)}
            </nav>`}
          ${here.rows.map((m) => html`
            <button key=${m.fixture_id} class="linky card rowbetween"
                    style="width:100%"
                    data-testid=${`player-recent-${m.fixture_id}`}
                    onClick=${() => onGo('fixture', leagueId, m.fixture_id)}>
              <span class="sub">${m.played_on}</span>
              <span>${m.opponent}</span>
              <span class="mono pts">
                ${m.score ? `${m.score.for}–${m.score.against}` : 'v'}
              </span>
              <span class="mono sub">
                ${`${cell(m.frames_won)}/${cell(m.frames_played)}`}
              </span>
            </button>`)}
        </div>`}
    </div>`;
}

// Not "0" -- an absent number reads as a fact about a real person. Same rule
// leagues.js's Stats board enforces for won/lost/win_pct (see its own
// `cell`), reused here rather than written a second way.
const cell = (v) => (v === null || v === undefined ? '—' : v);

/** One `{team, division}` pair (see cueforge/api.py's `teams_of`) as the
 * text this screen has always shown for a season's team -- team alone when
 * there is no division to add. The one-team and multi-team cases both
 * build their line from this, so a season with one team still reads
 * exactly as it did before `teams` existed. */
const teamLine = (t) => `${t.team}${t.division ? ` · ${t.division}` : ''}`;

/** A rating record's elo, rounded -- or the dash, both when there is no
 * record at all (an all-time book with nothing written for that kind yet)
 * AND when there is a record whose `elo` is itself null. The latter is not
 * hypothetical: `record && Math.round(record.elo)` used to skip straight to
 * `Math.round`, and `Math.round(null) === 0` -- a real person with an
 * unknown rating rendered as "0", a number that reads as a fact. Checking
 * `record.elo != null` on its own path, rather than folding it into the
 * `record &&` short-circuit, is what stops that. */
const elo = (record) => cell(
  record && record.elo != null ? Math.round(record.elo) : null);

/** A percentage, or the dash for an unknown one -- never a bare number that
 * could be mistaken for something else on the row. */
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

/** A W/L/rating line for one kind of play this season, or three dashes when
 * that kind was never played (`record` is null -- see cueforge/api.py's
 * `record()`, which returns null rather than a zeroed shape). */
const wl = (record) => (record === null || record === undefined
  ? '—'
  : `${cell(record.won)}–${cell(record.lost)} (${pct(record.win_pct)}) · `
    + `${elo(record)}`);
